import { useEffect, useRef, useState } from "react";
import { MoveHorizontal } from "lucide-react";

const ANGLES = [45, 90, 135, 180, 225, 270, 315, 0];

export default function MiniSpin({ prefix, name }) {
  const frames = ANGLES.map((a) => `/images/${prefix}-${a}.jpg`);
  const [idx, setIdx] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [spinning, setSpinning] = useState(true);
  const [grabbing, setGrabbing] = useState(false);
  const dragRef = useRef(null);
  const idleRef = useRef(null);

  useEffect(() => {
    setLoaded(false);
    let done = 0;
    frames.forEach((src) => {
      const im = new Image();
      im.onload = im.onerror = () => {
        done += 1;
        if (done === frames.length) setLoaded(true);
      };
      im.src = src;
    });
  }, [prefix]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!loaded || !spinning) return undefined;
    const iv = setInterval(() => setIdx((i) => (i + 1) % frames.length), 2200);
    return () => clearInterval(iv);
  }, [loaded, spinning, prefix]); // eslint-disable-line react-hooks/exhaustive-deps

  const wake = () => {
    setSpinning(false);
    clearTimeout(idleRef.current);
    idleRef.current = setTimeout(() => setSpinning(true), 4000);
  };

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = { x: e.clientX, startIdx: idx };
    setGrabbing(true);
    wake();
  };

  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const steps = Math.round((e.clientX - dragRef.current.x) / 40);
    if (steps !== 0) {
      const next = (((dragRef.current.startIdx + steps) % frames.length) + frames.length) % frames.length;
      setIdx(next);
    }
  };

  const onPointerUp = () => {
    dragRef.current = null;
    setGrabbing(false);
    wake();
  };

  return (
    <div
      className={`relative mt-3 h-40 touch-none select-none overflow-hidden rounded-xl border border-white/10 bg-[#0a0a0e] ${grabbing ? "cursor-grabbing" : "cursor-grab"}`}
      data-testid="booking-minispin"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
    >
      {frames.map((src, i) => (
        <img
          key={src}
          src={src}
          alt={`${name} ${ANGLES[i]}° view`}
          draggable="false"
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${loaded && i === idx ? "opacity-100" : "opacity-0"}`}
        />
      ))}
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#0a0a0e]">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#e6b800]/30 border-t-[#e6b800]" />
        </div>
      )}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-[#070709] to-transparent p-3">
        <p className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-white">
          {name} · {ANGLES[idx]}° · 360°
        </p>
        {!grabbing && (
          <p className="flex items-center gap-1.5 rounded-full border border-white/20 bg-black/50 px-2.5 py-1 font-mono2 text-[8px] uppercase tracking-[0.18em] text-neutral-300 backdrop-blur-sm">
            <MoveHorizontal size={11} className="text-[#e6b800]" /> Drag
          </p>
        )}
      </div>
    </div>
  );
}
